import { useState, useEffect } from 'react';
import { ToolLayout } from '../../components/tools/ToolLayout';
import { formatBrl } from '../../data/coins';

interface GasLevel {
  label: string;
  priorityGwei: number;
  totalGwei: number;
  transferCostBrl: number | null;
}

interface GasData {
  baseFeeGwei: number;
  levels: GasLevel[];
  ethPriceBrl: number | null;
  updatedAt: Date;
}

const RPC_URL = 'https://cloudflare-eth.com';
const TRANSFER_GAS = 21000;

const hexToGwei = (hex: string): number => parseInt(hex, 16) / 1e9;

export const GasPage: React.FC = () => {
  const [data, setData] = useState<GasData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchGas = async () => {
    try {
      const [feeRes, priceRes] = await Promise.all([
        fetch(RPC_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jsonrpc: '2.0',
            id: 1,
            method: 'eth_feeHistory',
            params: ['0x14', 'latest', [10, 50, 90]],
          }),
        }).then((r) => r.json()),
        fetch('https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=brl')
          .then((r) => r.json())
          .catch(() => null),
      ]);

      const history = feeRes?.result;
      if (!history?.baseFeePerGas?.length || !history?.reward?.length) {
        throw new Error('sem dados');
      }

      const baseFeeGwei = hexToGwei(history.baseFeePerGas[history.baseFeePerGas.length - 1]);

      // Média das priority fees por percentil nos últimos 20 blocos
      const rewards: string[][] = history.reward;
      const avgPriority = (index: number): number => {
        const values = rewards.map((r) => hexToGwei(r[index] || '0x0')).filter((v) => Number.isFinite(v));
        return values.reduce((sum, v) => sum + v, 0) / (values.length || 1);
      };

      const ethPriceBrl: number | null = priceRes?.ethereum?.brl ?? null;
      const toCostBrl = (totalGwei: number): number | null =>
        ethPriceBrl ? (totalGwei * TRANSFER_GAS * ethPriceBrl) / 1e9 : null;

      const makeLevel = (label: string, priorityGwei: number): GasLevel => {
        const totalGwei = baseFeeGwei + priorityGwei;
        return { label, priorityGwei, totalGwei, transferCostBrl: toCostBrl(totalGwei) };
      };

      setData({
        baseFeeGwei,
        levels: [
          makeLevel('Econômica', avgPriority(0)),
          makeLevel('Padrão', avgPriority(1)),
          makeLevel('Rápida', avgPriority(2)),
        ],
        ethPriceBrl,
        updatedAt: new Date(),
      });
      setError(null);
    } catch {
      setError('Não foi possível consultar a rede Ethereum agora. Tente novamente em instantes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGas();
    const interval = setInterval(fetchGas, 30_000);
    return () => clearInterval(interval);
  }, []);

  return (
    <ToolLayout
      path="/ferramentas/gas"
      title="Taxa de Gás do Ethereum"
      metaTitle="Taxa de Gás do Ethereum Agora (Gwei e em Reais)"
      metaDescription="Consulte a taxa de gás atual da rede Ethereum em gwei, com níveis de prioridade e custo estimado de uma transferência em reais, atualizado em tempo real."
      intro={[
        '"Gás" é a unidade que mede o custo computacional das operações na rede Ethereum. Toda transação paga uma taxa composta pela taxa-base da rede (queimada) mais uma gorjeta de prioridade para os validadores.',
        'Esta página consulta a rede Ethereum diretamente e mostra a taxa-base atual e três níveis de prioridade, com o custo estimado de uma transferência simples de ETH em reais.',
      ]}
      methodology={[
        'Dados obtidos por chamada direta à rede Ethereum (JSON-RPC público da Cloudflare), método eth_feeHistory dos últimos 20 blocos.',
        'Níveis de prioridade calculados pelos percentis 10, 50 e 90 das gorjetas pagas nos últimos blocos.',
        'Custo estimado = (taxa-base + prioridade) × 21.000 gás (transferência simples de ETH) × preço do ETH em BRL (CoinGecko).',
        'Interações com contratos (swaps, NFTs, DeFi) consomem mais gás — o custo real pode ser várias vezes maior.',
      ]}
      faqs={[
        {
          question: 'Por que o gás varia tanto?',
          answer:
            'A taxa-base sobe quando os blocos ficam cheios e cai quando esvaziam, a cada bloco (~12 segundos). Momentos de alta demanda — lançamentos, liquidações, picos de mercado — encarecem a rede rapidamente.',
        },
        {
          question: 'Como pagar menos gás?',
          answer:
            'Transacionar em horários de menor demanda, usar redes de segunda camada (Arbitrum, Optimism, Base) que herdam a segurança do Ethereum com taxas menores, ou agrupar operações. Veja o termo "Layer 2" no nosso glossário.',
        },
        {
          question: 'Esse custo vale para tokens e swaps?',
          answer:
            'Não — o valor exibido é de uma transferência simples de ETH (21.000 gás). Transferências de tokens ERC-20 consomem cerca do dobro; swaps em DEXs podem consumir 5 a 10 vezes mais.',
        },
      ]}
    >
      <div className="bg-white rounded-xl shadow p-6 md:p-8 mb-6">
        {loading ? (
          <div className="text-gray-500 text-center py-8">Consultando a rede Ethereum...</div>
        ) : error ? (
          <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">{error}</p>
        ) : data ? (
          <>
            <div className="text-center mb-6">
              <p className="text-sm text-gray-500 mb-1">Taxa-base atual</p>
              <p className="text-3xl font-bold text-gray-900">
                {data.baseFeeGwei.toFixed(2)} <span className="text-lg text-gray-500 font-semibold">gwei</span>
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {data.levels.map((level) => (
                <div key={level.label} className="border border-gray-200 rounded-xl p-4 text-center">
                  <p className="font-bold text-gray-900 mb-1">{level.label}</p>
                  <p className="text-2xl font-bold text-blue-700 mb-1">{level.totalGwei.toFixed(2)}</p>
                  <p className="text-xs text-gray-500 mb-2">gwei (base + {level.priorityGwei.toFixed(2)} prioridade)</p>
                  {level.transferCostBrl !== null && (
                    <p className="text-sm text-gray-700">
                      Transferência ETH: <strong>{formatBrl(level.transferCostBrl, 2)}</strong>
                    </p>
                  )}
                </div>
              ))}
            </div>

            <p className="text-xs text-gray-400 mt-4 text-center">
              Fontes: rede Ethereum via RPC público Cloudflare · preço ETH/BRL CoinGecko · atualizado às{' '}
              {data.updatedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}{' '}
              (recarrega a cada 30s)
            </p>
          </>
        ) : null}
      </div>
    </ToolLayout>
  );
};
